import { scaleSqrt } from 'd3';
import { extent } from 'd3-array';
import { useMemo } from 'react';

import {
  EntKind,
  MapCoordsGranularity,
  QUERY_SENTIMENT_V3,
  type QuerySentimentV3Query,
  type SentimentV3Ent,
} from '@revelio/data-access';

import { useSelectionListsForKeys } from '../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { buildSentimentV3VariablesFromEntities } from '../../filters/utils/get-sentiment-v3-variables';
import { getV3GeographyFilters } from '../../filters/utils/get-v3-geography-filters';
import type { LocationType, MapPoint } from '../Chart/Map/types';
import { MAX_RADIUS, MIN_RADIUS } from '../Chart/Map/utils/constants';
import { determineLocationType } from '../Chart/Map/utils/location-type-utils';
import type {
  SentimentMetricKeys,
  SentimentMetricValues,
} from '../PageConfigProvider/types/metric';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import {
  type GranularityWithIds,
  useCoordinateResolverForMixedGranularities,
  withCoordinateStatus,
} from './utils/coordinate-resolver';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';

type SentimentMapDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  metric: SentimentMetricValues;
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
};

const granularityToEntKind = (granularity: MapCoordsGranularity): EntKind => {
  switch (granularity) {
    case MapCoordsGranularity.Country:
      return EntKind.Country;
    case MapCoordsGranularity.Msa:
      return EntKind.Msa;
    case MapCoordsGranularity.Region:
      return EntKind.Region;
  }
};

export const SentimentMapDataProvider = ({
  primaryDimension,
  metric,
  children,
}: SentimentMapDataProviderProps) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListsForKeys(['country', 'msa']);
  const { country: countryList, msa: msaList } = selectionLists;
  const geographyFilter = effectiveFilters.find(
    (filter) => filter.id === FilterId.GEOGRAPHY
  );
  const geographyFilters = geographyFilter
    ? getV3GeographyFilters(geographyFilter)
    : {};

  // Restrict to Geography primary filter
  const isGeographyView = primaryDimension === FilterId.GEOGRAPHY;

  // Geographies to plot: no geo filter → all countries; only countries → those
  // countries + their MSAs; regions/MSAs → as selected.
  const geoGranularities = useMemo((): GranularityWithIds[] => {
    // Wait until both universe lists have loaded before enriching — they
    // arrive together from one geo-tree fetch.
    if (!countryList || !msaList) return [];

    const region = geographyFilters.region ?? [];
    const country = geographyFilters.country ?? [];
    const msa = geographyFilters.msa ?? [];

    if (!region.length && !country.length && !msa.length) {
      return [
        {
          granularity: MapCoordsGranularity.Country,
          ids: countryList.map((item) => Number(item.id)),
        },
      ];
    }

    if (country.length && !msa.length) {
      const enrichedMsa = msaList
        .filter((m) => country.includes(Number(m.country)))
        .map((m) => Number(m.id));
      return [
        { granularity: MapCoordsGranularity.Country, ids: country },
        { granularity: MapCoordsGranularity.Msa, ids: enrichedMsa },
      ];
    }

    const granularities: GranularityWithIds[] = [];
    if (region.length) {
      granularities.push({
        granularity: MapCoordsGranularity.Region,
        ids: region,
      });
    }
    if (country.length) {
      granularities.push({
        granularity: MapCoordsGranularity.Country,
        ids: country,
      });
    }
    if (msa.length) {
      granularities.push({
        granularity: MapCoordsGranularity.Msa,
        ids: msa,
      });
    }
    return granularities;
  }, [geographyFilters, countryList, msaList]);

  const requestEntities = useMemo(
    (): SentimentV3Ent[] =>
      geoGranularities.flatMap((granularity) =>
        granularity.ids.map((id) => ({
          EntID: id,
          EntType: granularityToEntKind(granularity.granularity),
        }))
      ),
    [geoGranularities]
  );

  const variables = useMemo(
    () =>
      buildSentimentV3VariablesFromEntities({
        entities: requestEntities,
        filters: effectiveFilters,
        primaryDimension,
        isSnapshot: true,
      }),
    [requestEntities, effectiveFilters, primaryDimension]
  );

  const [{ data, status }] = useUrqlQueryWithStatus<QuerySentimentV3Query>({
    query: QUERY_SENTIMENT_V3,
    variables,
    pause: !isGeographyView || requestEntities.length === 0,
  });

  const entities = useMemo(
    () => data?.sentiment_v3?.entities ?? [],
    [data?.sentiment_v3?.entities]
  );

  const metricKey: SentimentMetricKeys =
    metric === 'sentiment_top_bottom' ? 'overall' : metric;

  const geoData = useMemo(() => {
    return entities
      .map((entity) => {
        const id = entity.id;
        const label = entity.short_name || entity.long_name;
        const series = entity.metrics[metricKey];
        const last = series[series.length - 1];
        const value = last?.value ?? null;
        if (!id || value == null) return null;
        return { id, label, value: Number(value) };
      })
      .filter(
        (d): d is { id: number; label: string; value: number } => d !== null
      );
  }, [entities, metricKey]);

  const { data: coords, ...coordsState } =
    useCoordinateResolverForMixedGranularities(geoGranularities);

  const points = useMemo((): MapPoint[] => {
    if (!coords.length || !geoData.length) return [];
    const coordMap = new Map(coords.map((c) => [c.id, c] as const));
    const filtered = geoData.filter((g) => coordMap.has(g.id));

    const ext = extent(filtered.map((d) => d.value));
    const [minVal, maxVal] = ext as [number | undefined, number | undefined];
    if (minVal == null || maxVal == null) return [];
    const domainMax = maxVal === minVal ? minVal + 1 : maxVal;
    const scale = scaleSqrt()
      .domain([minVal, domainMax] as [number, number])
      .range([MIN_RADIUS, MAX_RADIUS]);

    const getLocationType = (
      label: string,
      coordGranularity: MapCoordsGranularity
    ): LocationType => {
      return determineLocationType(label, coordGranularity);
    };

    return filtered
      .map((d) => {
        const c = coordMap.get(d.id);
        if (!c) return null;
        const label = d.label || c.name;
        return {
          id: String(d.id),
          label,
          lat: c.lat,
          lon: c.lon,
          value: d.value / 100,
          circleRadius: scale(d.value),
          locationType: getLocationType(label, c.granularity),
        } as MapPoint;
      })
      .filter((p): p is MapPoint => p !== null);
  }, [coords, geoData]);

  return children({
    data: { points },
    ...withCoordinateStatus(
      status ?? DataProviderQueryStatus.Idle,
      coordsState
    ),
  });
};
